import { EventInput, EventClickArg } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormControlLabel,
  Switch,
  useTheme,
  useMediaQuery,
  Grid,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Chip,
} from "@mui/material";
import React, { useState, useEffect, useMemo } from "react";

import { useHandleCalendar } from "0.Mine/1.Hooks/business/CalendarEvent";
import { useBusinessContext } from "0.Mine/2.Context/businessContext";

// Define the structure for our appointment data
export interface AppointmentData {
  date: string;
  time_start: string;
  time_end: string;
  serviceName: string;
  customer: string;
  customerEmail: string;
  staffId: number | string;
  staffName: string;
  description?: string;
  eventId?: number;
  isCalendarEvent?: boolean;
}

// Define a structure for staff members
export interface StaffMember {
  id: number | string;
  name: string;
  color: string;
}

// Special constant for unassigned staff
const UNASSIGNED_STAFF_ID = -1;
const unassignedStaff: StaffMember = {
  id: UNASSIGNED_STAFF_ID,
  name: "Non assegnato",
  color: "#9e9e9e", // Gray color for unassigned events
};

// Random color generator for staff members
const getRandomColor = () => {
  const colors = [
    "#4CAF50",
    "#2196F3",
    "#FF9800",
    "#9C27B0",
    "#E91E63",
    "#3F51B5",
    "#009688",
    "#FF5722",
    "#607D8B",
    "#795548",
    "#CDDC39",
    "#00BCD4",
  ];
  return colors[Math.floor(Math.random() * colors.length)];
};

const BookingCalendar: React.FC = () => {
  // State for storing appointment data
  const [appointments, setAppointments] = useState<AppointmentData[]>([]);

  // State for filter selections
  const [selectedStaff, setSelectedStaff] = useState<(number | string)[]>([]);
  const [viewType, setViewType] = useState<"timeGridWeek" | "dayGridMonth">("timeGridWeek");
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);

  // State for event details modal
  const [selectedEvent, setSelectedEvent] = useState<any>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);

  // UI state
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // API hooks
  const { getStaffCalendar } = useHandleCalendar();
  const { session } = useBusinessContext();

  // Fetch data
  useEffect(() => {
    const fetchData = async () => {
      if (!session?.selectedBusiness?.id) return;

      try {
        const response = await getStaffCalendar(session.selectedBusiness.id);
        if (!response || !Array.isArray(response)) return;

        // Set appointments
        setAppointments(response);

        // Process staff members
        processStaffMembers(response);
      } catch (error) {
        console.error("Error fetching calendar data:", error);
      }
    };

    fetchData();
  }, [session]);

  // Process and deduplicate staff members from appointments
  const processStaffMembers = (data: AppointmentData[]) => {
    // Create a Map to deduplicate staff members by ID
    const staffMap = new Map<string | number, StaffMember>();

    // Process all appointments to extract staff info
    data.forEach((appointment) => {
      if (!appointment.staffId) return;

      const staffId = appointment.staffId;
      const staffIdString = String(staffId);

      // Only add if not already in the map
      if (!staffMap.has(staffId)) {
        staffMap.set(staffId, {
          id: staffId,
          name: appointment.staffName || `Staff ${staffIdString}`,
          color: getRandomColor(),
        });
      }
    });

    // Convert map to array and add unassigned option
    const staffArray = Array.from(staffMap.values());
    const allStaff = [unassignedStaff, ...staffArray];

    setStaffMembers(allStaff);
    // Select all staff by default
    setSelectedStaff(allStaff.map((staff) => staff.id));
  };

  // Filter events based on selected staff
  const filteredEvents = useMemo(() => {
    if (!appointments.length) return [];

    return appointments
      .filter((appointment) => {
        // If no staff selected, show nothing
        if (selectedStaff.length === 0) return false;

        // Check if unassigned is selected for appointments without staffId
        if (!appointment.staffId && selectedStaff.includes(UNASSIGNED_STAFF_ID)) {
          return true;
        }

        // Check if this staff is selected
        return appointment.staffId && selectedStaff.includes(Number(appointment.staffId));
      })
      .map((appointment) => {
        // Find the matching staff member
        const staff = staffMembers.find(
          (s) => s.id === appointment.staffId || s.id === Number(appointment.staffId)
        );

        // Format the title
        const customerPart = appointment.customer ? `${appointment.customer}: ` : "";
        const title = `${customerPart}${appointment.serviceName}`;

        // Handle date formatting - ensure proper ISO format
        const formatDate = (dateStr: string) => {
          // Check if date is already in ISO format
          if (dateStr.includes("T")) return dateStr;
          return dateStr;
        };

        return {
          title,
          start: `${formatDate(appointment.date)}T${appointment.time_start}`,
          end: `${formatDate(appointment.date)}T${appointment.time_end}`,
          backgroundColor: staff?.color || unassignedStaff.color,
          borderColor: staff?.color || unassignedStaff.color,
          textColor: "#FFFFFF",
          extendedProps: {
            ...appointment,
            staffName: appointment.staffName || staff?.name || unassignedStaff.name,
          },
        } as EventInput;
      });
  }, [appointments, selectedStaff, staffMembers]);

  // Handle staff filter change
  const handleStaffChange = (event: SelectChangeEvent<(number | string)[]>) => {
    setSelectedStaff(event.target.value as (number | string)[]);
  };

  // Toggle all staff selection
  const handleToggleAllStaff = () => {
    if (selectedStaff.length === staffMembers.length) {
      setSelectedStaff([]);
    } else {
      setSelectedStaff(staffMembers.map((staff) => staff.id));
    }
  };

  // Handle view type change
  const handleViewChange = () => {
    setViewType(viewType === "timeGridWeek" ? "dayGridMonth" : "timeGridWeek");
  };

  // Handle event click - show details
  const handleEventClick = (info: EventClickArg) => {
    setSelectedEvent(info.event);
    setDetailsOpen(true);
  };

  // Close details modal
  const handleCloseDetails = () => {
    setDetailsOpen(false);
  };

  // Loading state
  if (!session) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 3,
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Typography>Loading calendar...</Typography>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography variant="h5" gutterBottom>
        Calendario Appuntamenti
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Staff filter */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth size="small">
            <InputLabel id="staff-filter-label">Operatori</InputLabel>
            <Select
              labelId="staff-filter-label"
              id="staff-filter"
              multiple
              value={selectedStaff}
              onChange={handleStaffChange}
              input={<OutlinedInput label="Operatori" />}
              renderValue={(selected) => {
                if (selected.length === staffMembers.length) {
                  return "Tutti gli operatori";
                }

                return (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((id) => {
                      const staff = staffMembers.find((s) => s.id === id);
                      return staff ? (
                        <Chip
                          key={id}
                          label={staff.name}
                          size="small"
                          sx={{
                            backgroundColor: staff.color,
                            color: "#fff",
                          }}
                        />
                      ) : null;
                    })}
                  </Box>
                );
              }}
            >
              <MenuItem onClick={handleToggleAllStaff}>
                <Checkbox checked={selectedStaff.length === staffMembers.length} />
                <ListItemText primary="Seleziona tutti" />
              </MenuItem>

              <MenuItem disabled>
                <ListItemText primary="─────────────────" />
              </MenuItem>

              {staffMembers.map((staff) => (
                <MenuItem key={staff.id} value={staff.id}>
                  <Checkbox checked={selectedStaff.includes(staff.id)} />
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      width: 12,
                      height: 12,
                      borderRadius: 6,
                      bgcolor: staff.color,
                      mr: 1,
                    }}
                  />
                  <ListItemText primary={staff.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        {/* View type toggle */}
        <Grid item xs={12} md={6}>
          <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
            <FormControlLabel
              control={
                <Switch
                  checked={viewType === "dayGridMonth"}
                  onChange={handleViewChange}
                  color="primary"
                />
              }
              label={viewType === "timeGridWeek" ? "Vista settimanale" : "Vista mensile"}
            />
          </Box>
        </Grid>
      </Grid>

      {/* Calendar */}
      <Box sx={{ flex: 1, minHeight: "500px", "& .fc": { height: "100%" } }}>
        <FullCalendar
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={viewType}
          headerToolbar={{
            left: "prev,next today",
            center: "title",
            right: "dayGridMonth,timeGridWeek,timeGridDay",
          }}
          events={filteredEvents}
          eventClick={handleEventClick}
          height="auto"
          slotMinTime="08:00:00"
          slotMaxTime="20:00:00"
          allDaySlot={false}
          weekends={true}
          businessHours={{
            daysOfWeek: [1, 2, 3, 4, 5], // Monday - Friday
            startTime: "09:00",
            endTime: "18:00",
          }}
          locale="it"
          buttonText={{
            today: "Oggi",
            month: "Mese",
            week: "Settimana",
            day: "Giorno",
          }}
        />
      </Box>

      {/* Event Details Modal */}
      <Dialog open={detailsOpen} onClose={handleCloseDetails} maxWidth="sm" fullWidth>
        {selectedEvent && (
          <>
            <DialogTitle>Dettagli appuntamento</DialogTitle>

            <DialogContent dividers>
              <Typography variant="h6" gutterBottom>
                {selectedEvent.title}
              </Typography>

              <Box sx={{ my: 2 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Data:</strong> {new Date(selectedEvent.start).toLocaleDateString("it-IT")}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  <strong>Orario:</strong>{" "}
                  {new Date(selectedEvent.start).toLocaleTimeString("it-IT", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}{" "}
                  -{" "}
                  {new Date(selectedEvent.end).toLocaleTimeString("it-IT", {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>

                <Box sx={{ mt: 2, display: "flex", alignItems: "center" }}>
                  <Box
                    component="span"
                    sx={{
                      display: "inline-block",
                      width: 12,
                      height: 12,
                      borderRadius: 6,
                      bgcolor: selectedEvent.backgroundColor,
                      mr: 1,
                    }}
                  />
                  <Typography variant="body1">
                    <strong>Operatore:</strong> {selectedEvent.extendedProps.staffName}
                  </Typography>
                </Box>

                {selectedEvent.extendedProps.customer && (
                  <Typography variant="body1" gutterBottom sx={{ mt: 1 }}>
                    <strong>Cliente:</strong> {selectedEvent.extendedProps.customer}
                  </Typography>
                )}

                {selectedEvent.extendedProps.description && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body1" gutterBottom>
                      <strong>Descrizione:</strong>
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                      {selectedEvent.extendedProps.description}
                    </Typography>
                  </Box>
                )}
              </Box>
            </DialogContent>

            <DialogActions>
              <Button onClick={handleCloseDetails}>Chiudi</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Paper>
  );
};

export default BookingCalendar;
